// hooks/useDeleteNote.js
import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  deleteInlineImagesApi,
  deleteAttachedDocumentsApi,
  deleteNoteApi,
} from '../services/api';

export const useDeleteNote = (apiUrl, userId, refreshNotes, options = {}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback((note) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (note.userId && Number(note.userId) !== Number(user.id)) {
      toast.error("You can only delete notes you created.");
      return;
    }
    const hrs = (Date.now() - new Date(note.timeStamp)) / 36e5;
    if (hrs > 24) {
      toast.error("Cannot delete notes older than 24 hours");
    } else {
      setNoteToDelete(note);
      setShowDeleteConfirm(true);
    }
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!noteToDelete) return;
    setIsDeleting(true);
    try {
      // Delete inline images, attached documents, and the note itself
      await deleteInlineImagesApi(noteToDelete.id, apiUrl);
      await deleteAttachedDocumentsApi(noteToDelete.id, apiUrl);
      await deleteNoteApi(noteToDelete.id, userId, apiUrl);
      toast.success("Note and associated files deleted");

      // First refresh the main notes list
      await refreshNotes();

      // Execute stacked‑view specific logic if provided in options
      if (options.onAfterDelete) {
        await options.onAfterDelete(noteToDelete);
      }

      return true;
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.message || "Failed to delete note");
      return false;
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      setNoteToDelete(null);
    }
  }, [noteToDelete, apiUrl, userId, refreshNotes, options]);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteConfirm(false);
    setNoteToDelete(null);
  }, []);

  return {
    showDeleteConfirm,
    noteToDelete,
    isDeleting,
    handleDelete,
    handleConfirmDelete,
    closeDeleteModal,
  };
};